/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { OperationHandler } from 'src/core/services/api/createOperationCall';
import { EntityStore } from 'src/core/types/EntityStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { batchPromises, getNextAbleResults } from 'src/core/utils/batchPromises';
import { getCustomTextProjectService } from 'src/customText/core/services/projects/customTextProjectService';
import { CustomTextProjectJobStatusSwagger } from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { CustomTextModelEvaluationSwagger } from 'src/customText/core/services/trainedModels/customTextModelEvaluationSwagger';
import { CustomTextModelVerificationSwagger } from 'src/customText/core/services/trainedModels/customTextModelVerificationSwagger';
import { getCustomTextTrainedModelService } from 'src/customText/core/services/trainedModels/customTextTrainedModelService';
import { CustomTextClassificationStore } from 'src/customText/core/stores/CustomTextClassificationStore';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextExtractionStore } from 'src/customText/core/stores/CustomTextExtractionStore';
import { CustomTextModelVerificationStore } from 'src/customText/core/stores/CustomTextModelVerificationStore';
import { exportedProjectSwaggerToDocumentAndTagStores } from 'src/customText/modules/handlers/projects/projectMappers';
import { customTextModalGuidanceSwaggersToStore } from 'src/customText/modules/handlers/trainedModels/customTextModelGuidanceMappers';
import {
    customTextTrainedModelEvaluationSwaggerToModelEvaluationStore,
    customTextTrainedModelsSwaggerToStores,
    customTextTrainedModelVerificationSwaggerToStore
} from 'src/customText/modules/handlers/trainedModels/customTextTrainedModelMappers';
import { EvaluationJobStore } from 'src/modules/modelEvaluation/EvaluationJobInfoStore';
import { getEvaluationJobOperationHandler } from 'src/modules/modelEvaluation/utils/evaluationUtils';
import { TrainedModelSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

export type CustomTextTrainedModelHandlerGroup = ReturnType<typeof createCustomTextTrainedModelHandlerGroup>;

export const createCustomTextTrainedModelHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;

    const getExportSnapshotPollOperationHandler = (): OperationHandler<CustomTextProjectJobStatusSwagger> => ({
        retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
        handleCall: async data => {
            if (!lifetime.isDisposed && data.status === 'failed') {
                throw new Error(data.errors[0]?.message);
            }

            if (lifetime.isDisposed || data.status !== 'succeeded') {
                return;
            }

            const { currentCustomTextProjectStore } = rootStore;

            const results = await getCustomTextProjectService().getExportProjectResults({
                resultUrl: data.resultUrl
            });
            try {
                const { documents, tagStores } = exportedProjectSwaggerToDocumentAndTagStores(
                    results,
                    currentCustomTextProjectStore.documents,
                    currentCustomTextProjectStore.projectKind
                );

                let tagsStore: CustomTextExtractionStore | CustomTextClassificationStore;
                if (results.metadata.projectKind === 'CustomEntityRecognition' || results.metadata.projectKind === 'CustomHealthcare') {
                    tagsStore = new CustomTextExtractionStore(<EntityStore[]>tagStores);
                } else {
                    tagsStore = new CustomTextClassificationStore(<CustomTextClassStore[]>tagStores);
                }

                currentCustomTextProjectStore.updateProject(results.metadata, documents, tagsStore);
            } catch (e) {
                throw new Error(
                    'Error loading your model snapshot. Please make sure that your tags file in the container was not manually edited and try loading your model snapshot again.'
                );
            }
        }
    });

    const getCustomTextTrainedModels = async ({}: HandlerParams<object>) => {
        const { trainedModels, name: projectName } = rootStore.currentCustomTextProjectStore;

        if (trainedModels.items.length > 0) {
            return;
        }

        trainedModels.tracker.setState('running');

        const trainedModelsData = await getNextAbleResults<'value', TrainedModelSwagger>(
            (nextLink: string) =>
                getCustomTextTrainedModelService().getCustomTextTrainedModels({
                    nextLink,
                    projectName
                }),
            'value'
        );

        const trainedModelStores = customTextTrainedModelsSwaggerToStores(trainedModelsData);

        await Promise.all(
            trainedModelStores.map(model => {
                getTrainedModelEvaluationData({ trainedModelName: model.name });
                getTrainedModelGuidanceReport({ trainedModelName: model.name });
            })
        );

        transaction(() => {
            trainedModels.setItems(trainedModelStores);
            trainedModels.tracker.setState('completed');
        });
    };

    const getTrainedModelEvaluationData = async ({
        trainedModelName,
        isRefreshEvaluation = false
    }: HandlerParams<{ trainedModelName: string; isRefreshEvaluation?: boolean }>) => {
        if (!rootStore.currentCustomTextProjectStore) {
            return;
        }

        const { modelsEvaluationMap } = rootStore.currentCustomTextProjectStore;
        if (modelsEvaluationMap.has(trainedModelName) && !isRefreshEvaluation) {
            return;
        }

        const modelEvaluationData = await getCustomTextTrainedModelService().getEvaluationData({
            projectName: rootStore.currentCustomTextProjectStore.name,
            trainedModelName
        });

        const modelEvaluationStore = customTextTrainedModelEvaluationSwaggerToModelEvaluationStore(
            modelEvaluationData as CustomTextModelEvaluationSwagger
        );

        rootStore.currentCustomTextProjectStore?.addModelEvaluationMapItem(trainedModelName, modelEvaluationStore);
    };

    const getTrainedModelVerificationData = async ({
        trainedModelName,
        localStore
    }: HandlerParams<{ trainedModelName: string; localStore: CustomTextModelVerificationStore }>) => {
        localStore.setVerificationDocuments([]);

        const { name: projectName, projectKind, classificationStore, extractionStore } = rootStore.currentCustomTextProjectStore;
        const tagStores =
            projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
                ? extractionStore.entities
                : classificationStore.classes;

        const modelVerificationData = await getNextAbleResults<'value', CustomTextModelVerificationSwagger>(
            (nextLink: string) =>
                getCustomTextTrainedModelService().getVerificationData({
                    nextLink,
                    projectName,
                    trainedModelName
                }),
            'value'
        );

        const documents = customTextTrainedModelVerificationSwaggerToStore(modelVerificationData, tagStores);
        localStore.setVerificationDocuments(documents);
    };

    const getTrainedModelGuidanceReport = async ({
        trainedModelName,
        isRefreshEvaluation = false
    }: HandlerParams<{ trainedModelName: string; isRefreshEvaluation?: boolean }>) => {
        if (!rootStore.currentCustomTextProjectStore) {
            return;
        }

        const { modelsGuidanceMap } = rootStore.currentCustomTextProjectStore;
        if (modelsGuidanceMap.has(trainedModelName) && !isRefreshEvaluation) {
            return;
        }

        const modelsGuidanceData = await getCustomTextTrainedModelService().getTrainedModelGuidanceReport({
            projectName: rootStore.currentCustomTextProjectStore.name,
            trainedModelName
        });
        const modelsGuidanceStore = customTextModalGuidanceSwaggersToStore(trainedModelName, modelsGuidanceData);
        rootStore.currentCustomTextProjectStore?.addModelGuidanceMapItem(trainedModelName, modelsGuidanceStore);
    };

    const getTrainedModelAllData = async ({
        trainedModelName,
        isRefreshEvaluation = false
    }: HandlerParams<{ trainedModelName: string; isRefreshEvaluation?: boolean }>) => {
        if (!rootStore.currentCustomTextProjectStore) {
            return;
        }

        await Promise.all([
            getTrainedModelEvaluationData({ trainedModelName, isRefreshEvaluation }),
            getTrainedModelGuidanceReport({ trainedModelName, isRefreshEvaluation })
        ]);
    };

    const deleteCustomTextTrainedModels = async ({
        projectName,
        modelNames,
        generateMultiFailureMessage
    }: HandlerParams<{ projectName: string; modelNames: string[]; generateMultiFailureMessage: (failedCount: number) => string }>) => {
        const { customTextProjectStoreHouse: customTextProjectsStoreHouse } = rootStore;
        const trainedModelsStoreHouse = customTextProjectsStoreHouse.findById(projectName).trainedModels;
        trainedModelsStoreHouse.tracker.setState('running');

        const { results, failedCount } = await batchPromises(
            modelNames.map(modelName => getCustomTextTrainedModelService().deleteTrainedModel({ projectName, trainedModelName: modelName }))
        );

        const deletedModelsNames = results.filter(res => res.status === 'success').map((_, index) => modelNames[index]);

        if (failedCount > 0) {
            throw new Error(generateMultiFailureMessage(failedCount));
        }

        transaction(() => {
            trainedModelsStoreHouse.setItems(trainedModelsStoreHouse.items.filter(m => deletedModelsNames.every(name => m.name !== name)));
            trainedModelsStoreHouse.tracker.setState('completed');
        });
    };

    const loadCustomTextSnapshotModel = async ({
        projectName,
        trainedModelName
    }: HandlerParams<{ projectName: string; trainedModelName: string }>) => {
        const { trainedModels } = rootStore.currentCustomTextProjectStore;
        const trainedModelStore = trainedModels.items.find(trainedModel => trainedModel.name === trainedModelName);

        if (trainedModelStore.hasSnapshot) {
            const [_, exportStatusUrl] = await Promise.all([
                getCustomTextTrainedModelService().loadModelSnapshotAndPoll({
                    projectName,
                    trainedModelName,
                    operationHandler: {
                        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                        retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                        handleCall: data => {
                            if (data.status === 'failed') {
                                const error = data.errors?.[0];
                                throw new Error(`${error.message} ${error.innererror ? `: ${error.innererror.message}` : ''}`);
                            }
                        }
                    }
                }),
                getCustomTextProjectService().initiateExportProject({
                    projectName,
                    assetsToExport: ['all'],
                    showAutoTag: true,
                    trainedModelName
                })
            ]);

            await getCustomTextProjectService().pollExportProject({
                statusUrl: exportStatusUrl,
                operationHandler: getExportSnapshotPollOperationHandler()
            });
        } else {
            throw new Error("Model doesn't have a snapshot. Please retrain the model.");
        }
    };

    const refreshEvaluationCustomText = async ({
        projectName,
        trainedModelName
    }: HandlerParams<{ projectName: string; trainedModelName: string }>) => {
        let jobId: string;
        const resourceId = rootStore.azureStore.session.csAccountStore?.path;

        if (resourceId === null) {
            throw new Error('No resource id. Please retrain the model');
        }
        try {
            const statusUrl = await getCustomTextTrainedModelService().initiateRefreshEvaluation({
                projectName,
                trainedModelName,
                resourceId
            });

            jobId = new URL(statusUrl).pathname.split('/').pop();
            rootStore.currentCustomTextProjectStore.setEvaluationJobStore(
                new EvaluationJobStore(jobId, { status: 'inProgress', modelId: trainedModelName })
            );

            await getCustomTextTrainedModelService().pollRefreshEvaluation({
                statusUrl,
                operationHandler: getEvaluationJobOperationHandler(lifetime, rootStore.currentCustomTextProjectStore.evaluationJobStore)
            });

            if (rootStore.currentCustomTextProjectStore.evaluationJobStore?.status === 'succeeded') {
                await getTrainedModelAllData({
                    trainedModelName: rootStore.currentCustomTextProjectStore.evaluationJobStore?.modelId,
                    isRefreshEvaluation: true
                });
            }
        } catch (e) {
            rootStore.currentCustomTextProjectStore?.evaluationJobStore?.setStatus('failed');
        }
    };

    return {
        getCustomTextTrainedModels,
        getTrainedModelAllData,
        getTrainedModelGuidanceReport,
        getTrainedModelEvaluationData,
        getTrainedModelVerificationData,
        deleteCustomTextTrainedModels,
        loadCustomTextSnapshotModel,
        refreshEvaluationCustomText
    };
};
